import React from 'react';
import { Offcanvas, Badge, Table } from 'react-bootstrap';
import { format, parseISO } from 'date-fns';
import { LogData } from '../../../services/log/response/LogResponse';

// A handful of very old rows in `logs` predate structured action naming and
// were written with an empty string — shown as a readable fallback here
// instead of a blank drawer title (mirrors LogTable's badge fallback).
const formatActionLabel = (action: string | null | undefined): string => {
    const trimmed = (action ?? '').trim();
    if (!trimmed) return 'Unknown action';
    return trimmed.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
};

interface LogDetailDrawerProps {
    show: boolean;
    onClose: () => void;
    log: LogData | null;
    /** Optional — lets the caller filter the list by this log's correlation_id. */
    onViewRelated?: (correlationId: string) => void;
}

const formatDateLocal = (dateString?: string | null) => {
    if (!dateString) return '-';
    try {
        return format(parseISO(dateString), 'dd MMM yyyy, HH:mm:ss');
    } catch {
        return dateString;
    }
};

// A short, friendly label for the raw model_type value — falls back to the
// raw string itself for anything not in this list (e.g. a future module).
const MODEL_TYPE_LABELS: Record<string, string> = {
    'App\\Models\\BookingRoom': 'Reservation',
    'App\\Models\\V4\\BookingRoom': 'Reservation',
    'App\\Models\\V4\\RoomType': 'Room Type',
    'App\\Models\\V4\\RatePlan': 'Rate Plan / Link Rate',
    'App\\Models\\V4\\RoomMaintenance': 'Room Maintenance',
    'App\\Models\\V4\\Availability': 'Availability',
    'App\\Models\\V4\\Restriction': 'Restriction',
    'App\\Models\\V4\\DynamicPricingGroup': 'Dynamic Pricing',
    'App\\Models\\V4\\DynamicPricingRule': 'Dynamic Pricing',
    'App\\Models\\V4\\DynamicPricingPeriod': 'Dynamic Pricing',
    'App\\Models\\V4\\Inventory': 'Inventory',
    'App\\Models\\V4\\InventoryCategory': 'Inventory',
    'App\\Models\\V4\\Property': 'Property',
    'App\\Models\\Property': 'Property',
    'App\\Models\\V4\\User': 'User Access',
    'App\\Models\\Agent': 'Agent',
    'App\\Models\\PropertyBank': 'Property Bank',
    'App\\Models\\V4\\RateImport': 'Rate Import',
    booking_import: 'Booking Import',
    channex_availability_sync: 'Channex Sync — Availability',
    channex_restriction_sync: 'Channex Sync — Restriction',
    channex_api_call: 'Channex Sync',
    system: 'System',
};

// Renders old_value/new_value as a readable Field | Before | After table —
// same data as LogTable's inline "Changes" column, just not truncated.
const ChangesTable: React.FC<{ oldValue?: Record<string, unknown> | null; newValue?: Record<string, unknown> | null }> = ({ oldValue, newValue }) => {
    const keys = Array.from(new Set([...Object.keys(oldValue ?? {}), ...Object.keys(newValue ?? {})]));

    if (keys.length === 0) {
        return <p className="text-muted mb-0">No before/after values for this entry — it was recorded before structured change tracking, or the action doesn't modify data. See the description below.</p>;
    }

    return (
        <Table size="sm" bordered className="mb-0">
            <thead>
                <tr>
                    <th>Field</th>
                    <th>Before</th>
                    <th>After</th>
                </tr>
            </thead>
            <tbody>
                {keys.map((key) => (
                    <tr key={key}>
                        <td className="text-muted">{key}</td>
                        <td>{oldValue?.[key] === undefined ? <span className="text-muted">—</span> : String(oldValue[key])}</td>
                        <td className="fw-semibold">{newValue?.[key] === undefined ? <span className="text-muted">—</span> : String(newValue[key])}</td>
                    </tr>
                ))}
            </tbody>
        </Table>
    );
};

/**
 * Full detail for one log row — the "proof" for Channex sync rows (the
 * exact payload sent and response received) lives in `context` and was
 * previously stored but never shown anywhere. Shared between the general
 * Activity Log page and the per-reservation Log History tab (both render
 * rows via LogTable, which opens this).
 */
const LogDetailDrawer: React.FC<LogDetailDrawerProps> = ({ show, onClose, log, onViewRelated }) => {
    if (!log) return null;

    const moduleLabel = MODEL_TYPE_LABELS[log.model_type] ?? log.model_type;
    const hasContext = log.context && Object.keys(log.context).length > 0;

    return (
        <Offcanvas show={show} onHide={onClose} placement="end" style={{ width: 'min(560px, 100vw)' }}>
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>
                    <div className="fw-bold text-capitalize">{formatActionLabel(log.action)}</div>
                    <div className="small text-muted">{formatDateLocal(log.created_at)}</div>
                </Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                <section className="mb-4">
                    <h6 className="text-muted text-uppercase small mb-2">Who &amp; where</h6>
                    <dl className="row mb-0">
                        <dt className="col-4 text-muted">User</dt>
                        <dd className="col-8">{log.user?.name ?? <span className="text-muted">System / automated</span>}</dd>

                        <dt className="col-4 text-muted">IP address</dt>
                        <dd className="col-8">{log.ip_address ?? <span className="text-muted">—</span>}</dd>

                        <dt className="col-4 text-muted">Device</dt>
                        <dd className="col-8 text-break">{log.user_agent ?? <span className="text-muted">—</span>}</dd>
                    </dl>
                </section>

                <section className="mb-4">
                    <h6 className="text-muted text-uppercase small mb-2">What</h6>
                    <dl className="row mb-0">
                        <dt className="col-4 text-muted">Module</dt>
                        <dd className="col-8"><Badge bg="secondary" text="white">{moduleLabel}</Badge></dd>

                        <dt className="col-4 text-muted">Record ID</dt>
                        <dd className="col-8"><code className="small">{log.model_id}</code></dd>

                        {log.invoice_number && (
                            <>
                                <dt className="col-4 text-muted">Invoice</dt>
                                <dd className="col-8">{log.invoice_number}</dd>
                            </>
                        )}
                    </dl>
                </section>

                <section className="mb-4">
                    <h6 className="text-muted text-uppercase small mb-2">What changed</h6>
                    <ChangesTable oldValue={log.old_value} newValue={log.new_value} />
                    {log.log && <p className="small text-muted mt-2 mb-0">{log.log}</p>}
                </section>

                {hasContext && (
                    <section className="mb-4">
                        <h6 className="text-muted text-uppercase small mb-2">Proof (sent to / received from Channex)</h6>
                        {/* Tabler styles <pre> with a light text color meant for a dark
                            background, so on bg-light the JSON was near-invisible until
                            selected. Colors are set explicitly to keep it readable. */}
                        <pre className="border rounded p-2 small mb-0" style={{ maxHeight: 320, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word', backgroundColor: '#f6f8fb', color: '#182433' }}>
                            {JSON.stringify(log.context, null, 2)}
                        </pre>
                    </section>
                )}

                {log.correlation_id && (
                    <section>
                        <h6 className="text-muted text-uppercase small mb-2">Correlation</h6>
                        <p className="small mb-2">
                            Every row from this same action (the local change and whatever it triggered
                            downstream, e.g. a Channex sync) shares this id.
                        </p>
                        <code className="small d-block mb-2">{log.correlation_id}</code>
                        {onViewRelated && (
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => onViewRelated(log.correlation_id as string)}
                            >
                                View all related actions
                            </button>
                        )}
                    </section>
                )}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default LogDetailDrawer;
